import { Capacitor } from "@capacitor/core";
import { isBankIdResumeUrl, parseExternalUrlToAppRoute } from "@/lib/native/parseAppUrl";
import { stashPendingBankIdReturn } from "@/lib/native/pendingBankIdReturn";

/**
 * Capture Android/iOS launch / deep-link URLs as early as possible — before
 * NativeHashBootstrap can treat a cold start as a guest bounce to /auctions.
 *
 * On Android, BankID Custom Tabs often kills the WebView process; the Intent
 * URL arrives via getLaunchUrl / appUrlOpen after React mounts. Stashing the
 * query into localStorage lets Auth restore it even if the hash was wiped.
 */
let started = false;

function stashFromRawUrl(rawUrl: string): void {
  if (!rawUrl || isBankIdResumeUrl(rawUrl)) return;
  const route = parseExternalUrlToAppRoute(rawUrl);
  if (!route) return;
  const qIndex = route.indexOf("?");
  if (qIndex < 0) return;
  const pathPart = route.slice(0, qIndex);
  const queryPart = route.slice(qIndex + 1);
  if (!pathPart.includes("auth") || !queryPart) return;
  if (
    !queryPart.includes("nativeCode=") &&
    !queryPart.includes("bankid=") &&
    !queryPart.includes("bankidError=") &&
    !queryPart.includes("bankidRegister=") &&
    !queryPart.includes("bankidRegisterError=")
  ) {
    return;
  }
  const search = `?${queryPart}`;
  stashPendingBankIdReturn(search);
  console.info("[captureNativeLaunchUrl] stashed", search);

  // Apply into the hash immediately so bootstrap preserves /auth?…
  const nextHash = `#/auth${search}`;
  if (window.location.hash !== nextHash) {
    try {
      window.location.replace(
        `${window.location.pathname}${window.location.search}${nextHash}`,
      );
    } catch {
      /* ignore */
    }
  }
}

export function startNativeLaunchUrlCapture(): void {
  if (started) return;
  if (typeof window === "undefined") return;
  if (!Capacitor.isNativePlatform()) return;
  started = true;

  void import("@capacitor/app").then(({ App }) => {
    void App.getLaunchUrl()
      .then((result) => {
        if (result?.url) stashFromRawUrl(result.url);
      })
      .catch(() => undefined);

    void App.addListener("appUrlOpen", (event) => {
      if (event?.url) stashFromRawUrl(event.url);
    });
  });
}
